import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-meetings-board-dashboard',
  templateUrl: './meetings-board-dashboard.component.html',
  styleUrls: ['./meetings-board-dashboard.component.css'],
})
export class MeetingsBoardDashboardComponent implements OnInit {

  activeTab: string = 'meetings-board';
  activeMeetingType: string = 'board';

  companyName: string = 'DigiCompany Ltd';
  currentPeriod: string = 'June 2026 · FY27 Q1';

  calendarYear = 2026;
  calendarMonth = 5; // June (0-indexed)

  calendarMeetings: { [key: number]: { label: string; type: string; preponed?: boolean }[] } = {
    3:  [{ label: 'Audit', type: 'audit' }],
    11: [{ label: 'Standup', type: 'board' }],
    12: [{ label: 'NRC', type: 'nrc', preponed: true }],
    26: [{ label: 'Board', type: 'board' }],
  };

  get calendarCells(): { day: number; prevMonth?: boolean; nextMonth?: boolean }[] {
    const firstDay = new Date(this.calendarYear, this.calendarMonth, 1).getDay();
    const daysInMonth = new Date(this.calendarYear, this.calendarMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(this.calendarYear, this.calendarMonth, 0).getDate();
    const cells: { day: number; prevMonth?: boolean; nextMonth?: boolean }[] = [];
    for (let i = firstDay - 1; i >= 0; i--) {
      cells.push({ day: daysInPrevMonth - i, prevMonth: true });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({ day: d });
    }
    let nextDay = 1;
    while (cells.length % 7 !== 0) {
      cells.push({ day: nextDay++, nextMonth: true });
    }
    return cells;
  }

  committeeFrequency = [
    { name: 'Audit committee',       held: 1, required: 4, gap: '≤120d gap', status: 'ok' },
    { name: 'NRC',                   held: 0, required: 1, gap: '≤365d gap', status: 'warn' },
    { name: 'Stakeholder relations', held: 1, required: 4, gap: '≤90d gap',  status: 'ok' },
    { name: 'CSR',                   held: 0, required: 2, gap: 'gap exceeded', status: 'breach' },
    { name: 'Risk management',       held: 0, required: 2, gap: '≤180d gap', status: 'warn' },
  ];

  committeeFrequencyBars(item: any): { filled: boolean }[] {
    return Array.from({ length: item.required }, (_, i) => ({ filled: i < item.held }));
  }

  actionItems = [
    { title: "Submit auditor's response on going-concern note", source: 'Audit Committee · 18 Apr', status: 'overdue',     statusLabel: '10d overdue', initials: 'DK', avatarBg: '#F7C1C1', avatarColor: '#501313', accentColor: '#E24B4A' },
    { title: 'Finalise ESOP grant list for FY27',               source: 'NRC · 22 Apr',            status: 'due-soon',    statusLabel: 'Due in 2d',   initials: 'RM', avatarBg: '#FAC775', avatarColor: '#412402', accentColor: '#EF9F27' },
    { title: 'Circulate revised related-party policy',          source: 'Board · 28 Apr',           status: 'in-progress', statusLabel: 'In progress', initials: 'VS', avatarBg: '#B5D4F4', avatarColor: '#042C53', accentColor: '#378ADD' },
    { title: 'CSR spend reconciliation Q4 FY26',                source: 'CSR · 12 Apr',             status: 'closed',      statusLabel: 'Closed',      initials: 'AP', avatarBg: '#C0DD97', avatarColor: '#173404', accentColor: '#1D9E75' },
  ];

  allMeetingsTimeline = [
    { month: 'Apr', dots: ['board','audit'], past: true },
    { month: 'May', dots: ['audit','nrc','src'], past: true },
    { month: 'Jun', dots: ['audit','board'], current: true },
    { month: 'Jul', dots: [], scheduled: false },
    { month: 'Aug', dots: [], warn: true },
    { month: 'Sep', dots: [] },
    { month: 'Oct', dots: [] },
    { month: 'Nov', dots: [] },
    { month: 'Dec', dots: [] },
    { month: 'Jan', dots: [] },
    { month: 'Feb', dots: [] },
    { month: 'Mar', dots: [] },
  ];

  meetingTypeTabs = [
    { key: 'board', label: 'Board' },
    { key: 'audit', label: 'Audit committee' },
    { key: 'nrc',   label: 'NRC' },
    { key: 'src',   label: 'Stakeholder rel.' },
    { key: 'csr',   label: 'CSR' },
    { key: 'risk',  label: 'Risk mgmt' },
  ];

  complianceData: { [key: string]: any } = {
    board: {
      title: 'Board meeting compliance · FY27',
      rule: 'Rule · 4 meetings per FY · max 120-day gap · at least 1 per calendar quarter',
      statusLabel: '● On track', statusClass: 'ok',
      quarters: [
        { q: 'Q1', period: 'APR–JUN', state: 'ok',      primary: '1 meeting held',    secondary: '28 Apr 2026',             note: '+ 26 Jun scheduled', action: false },
        { q: 'Q2', period: 'JUL–SEP', state: 'urgent',  primary: 'Schedule needed',   secondary: 'By 26 Aug to avoid gap',  note: '0 / required ≥1',    action: true },
        { q: 'Q3', period: 'OCT–DEC', state: 'pending', primary: 'Not yet planned',   secondary: '0 / required ≥1',         note: '—',                  action: true },
        { q: 'Q4', period: 'JAN–MAR', state: 'pending', primary: 'Not yet planned',   secondary: '0 / required ≥1',         note: '—',                  action: true },
      ],
      gap: { title: 'Gap since last board meeting', subtitle: 'Last held · 28 Apr 2026 · Section 173(1) Companies Act', current: 53, max: 120, warn: 90, remaining: 67, scheduleBy: '26 Aug 2026', suggested: '13 Aug 2026', nextScheduled: '26 Jun 2026', urgent: false },
    },
    audit: {
      title: 'Audit committee compliance · FY27',
      rule: 'Rule from committee master · 4 meetings per FY · max 120-day gap',
      statusLabel: '● On track', statusClass: 'ok',
      quarters: [
        { q: 'Q1', period: 'APR–JUN', state: 'ok',      primary: '3 meetings held',  secondary: '15 Apr · 8 May · 3 Jun', note: 'Target met',       action: false },
        { q: 'Q2', period: 'JUL–SEP', state: 'pending', primary: 'Not yet planned',  secondary: '0 / required ≥1',        note: '—',                action: true },
        { q: 'Q3', period: 'OCT–DEC', state: 'pending', primary: 'Not yet planned',  secondary: '0 / required ≥1',        note: '—',                action: true },
        { q: 'Q4', period: 'JAN–MAR', state: 'pending', primary: 'Not yet planned',  secondary: '0 / required ≥1',        note: '—',                action: true },
      ],
      gap: { title: 'Gap since last audit committee meeting', subtitle: 'Last held · 3 Jun 2026 · Section 177 Companies Act', current: 17, max: 120, warn: 90, remaining: 103, scheduleBy: '1 Oct 2026', suggested: '18 Sep 2026', nextScheduled: 'Not scheduled', urgent: false },
    },
    nrc: {
      title: 'NRC compliance · FY27',
      rule: 'Rule from committee master · 1 meeting per FY · max 365-day gap',
      statusLabel: '● Schedule soon', statusClass: 'warn',
      quarters: [
        { q: 'Q1', period: 'APR–JUN', state: 'ok',      primary: '1 meeting held',  secondary: '14 May 2026',              note: 'Annual target met',     action: false },
        { q: 'Q2', period: 'JUL–SEP', state: 'pending', primary: 'No target',       secondary: 'Annual frequency only',    note: '—',                     action: false },
        { q: 'Q3', period: 'OCT–DEC', state: 'pending', primary: 'No target',       secondary: 'Annual frequency only',    note: '—',                     action: false },
        { q: 'Q4', period: 'JAN–MAR', state: 'pending', primary: 'No target',       secondary: 'Annual frequency only',    note: '—',                     action: false },
      ],
      gap: { title: 'Gap since last NRC meeting', subtitle: 'Last held · 14 May 2026 · committee master', current: 37, max: 365, warn: 300, remaining: 328, scheduleBy: '14 May 2027', suggested: '7 Apr 2027', nextScheduled: 'Not scheduled', urgent: false },
    },
    src: {
      title: 'Stakeholder relations committee · FY27',
      rule: 'Rule from committee master · 4 meetings per FY · max 90-day gap',
      statusLabel: '● On track', statusClass: 'ok',
      quarters: [
        { q: 'Q1', period: 'APR–JUN', state: 'ok',      primary: '1 meeting held',    secondary: '22 May 2026',           note: 'Target met',       action: false },
        { q: 'Q2', period: 'JUL–SEP', state: 'urgent',  primary: 'Schedule needed',   secondary: 'By 20 Aug · 90d rule',  note: '0 / required ≥1', action: true },
        { q: 'Q3', period: 'OCT–DEC', state: 'pending', primary: 'Not yet planned',   secondary: '0 / required ≥1',       note: '—',                action: true },
        { q: 'Q4', period: 'JAN–MAR', state: 'pending', primary: 'Not yet planned',   secondary: '0 / required ≥1',       note: '—',                action: true },
      ],
      gap: { title: 'Gap since last SRC meeting', subtitle: 'Last held · 22 May 2026 · committee master', current: 29, max: 90, warn: 70, remaining: 61, scheduleBy: '20 Aug 2026', suggested: '13 Aug 2026', nextScheduled: 'Not scheduled', urgent: false },
    },
    csr: {
      title: 'CSR committee compliance · FY27',
      rule: 'Rule from committee master · 2 meetings per FY · max 180-day gap',
      statusLabel: '● Gap exceeded', statusClass: 'breach',
      quarters: [
        { q: 'Q1', period: 'APR–JUN', state: 'urgent',  primary: 'Schedule needed',   secondary: '186 days since last',   note: '0 held this FY',   action: true },
        { q: 'Q2', period: 'JUL–SEP', state: 'pending', primary: 'Not yet planned',   secondary: '0 / required 1',        note: '—',                action: true },
        { q: 'Q3', period: 'OCT–DEC', state: 'pending', primary: 'No target',         secondary: 'Semi-annual frequency', note: '—',                action: false },
        { q: 'Q4', period: 'JAN–MAR', state: 'pending', primary: 'No target',         secondary: 'Semi-annual frequency', note: '—',                action: false },
      ],
      gap: { title: 'Gap since last CSR meeting', subtitle: 'Last held · 16 Dec 2025 · committee master', current: 186, max: 180, warn: 150, remaining: -6, scheduleBy: '14 Jun 2026', suggested: 'Overdue', nextScheduled: 'Not scheduled', urgent: true },
    },
    risk: {
      title: 'Risk management committee · FY27',
      rule: 'Rule from committee master · 2 meetings per FY · max 180-day gap',
      statusLabel: '● Pending start', statusClass: 'pending',
      quarters: [
        { q: 'Q1', period: 'APR–JUN', state: 'pending', primary: 'Not yet planned',   secondary: '0 / required ≥1',       note: '—', action: true },
        { q: 'Q2', period: 'JUL–SEP', state: 'pending', primary: 'Not yet planned',   secondary: '0 / required ≥1',       note: '—', action: true },
        { q: 'Q3', period: 'OCT–DEC', state: 'pending', primary: 'No target',         secondary: 'Semi-annual',           note: '—', action: false },
        { q: 'Q4', period: 'JAN–MAR', state: 'pending', primary: 'No target',         secondary: 'Semi-annual',           note: '—', action: false },
      ],
      gap: { title: 'Gap since last risk meeting', subtitle: 'Last held · 12 Feb 2026 · committee master', current: 128, max: 180, warn: 150, remaining: 52, scheduleBy: '11 Aug 2026', suggested: '4 Aug 2026', nextScheduled: 'Not scheduled', urgent: false },
    },
  };

  get activeCompliance() { return this.complianceData[this.activeMeetingType] || this.complianceData['board']; }

  get gapFillPct(): number {
    const g = this.activeCompliance.gap;
    return Math.min(100, Math.max(0, (g.current / g.max) * 100));
  }

  get gapWarnPct(): number {
    const g = this.activeCompliance.gap;
    return (g.warn / g.max) * 100;
  }

  get gapFillClass(): string {
    const g = this.activeCompliance.gap;
    if (g.current >= g.max) return 'breach';
    if (g.current >= g.warn) return 'warn';
    return 'ok';
  }

  constructor(private router: Router, private route: ActivatedRoute) {}

  ngOnInit() {
    const stored = localStorage.getItem('companyDetails');
    if (stored) {
      try { this.companyName = JSON.parse(stored).companyName || this.companyName; } catch (e) {}
    }
  }

  setTab(tab: string) { this.activeTab = tab; }

  setMeetingType(key: string) { this.activeMeetingType = key; }

  committeeBarFilled(item: any): number {
    return Math.min(item.held, item.required);
  }

  scheduleMeeting() {}
  draftAgenda() {}
  uploadBoardPack() {}
  generateMinutes() {}
}
